// Procedural first-person models: a USP-style pistol with silencer, and gloved hands with sleeves.
// Built from simple primitives so they ship with the site (no game assets). Units are viewmodel
// camera units; the pistol points down -Z with +Y up.

import * as THREE from 'three';

export interface PistolModel {
  group: THREE.Group;
  slide: THREE.Group;
  mag: THREE.Mesh;
  silencer: THREE.Mesh;
  /** Where the muzzle flash goes (end of the barrel or the silencer). */
  muzzle: THREE.Object3D;
}

const box = (w: number, h: number, d: number, mat: THREE.Material, x = 0, y = 0, z = 0) => {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  return m;
};

export function buildPistol(): PistolModel {
  const polymer = new THREE.MeshPhongMaterial({ color: 0x2b2d31, specular: 0x222222, shininess: 18 });
  const steel = new THREE.MeshPhongMaterial({ color: 0x3c4046, specular: 0x9aa3ad, shininess: 70 });
  const dark = new THREE.MeshPhongMaterial({ color: 0x1a1b1e, specular: 0x555a60, shininess: 45 });
  const group = new THREE.Group();

  // Slide with serrations at the back and an ejection port.
  const slide = new THREE.Group();
  slide.add(box(0.034, 0.04, 0.33, steel, 0, 0.022, -0.115));
  for (let i = 0; i < 7; i++) slide.add(box(0.036, 0.034, 0.004, dark, 0, 0.022, 0.03 - i * 0.009));
  slide.add(box(0.012, 0.012, 0.05, dark, 0.012, 0.038, -0.07)); // ejection port
  slide.add(box(0.012, 0.01, 0.008, dark, 0, 0.046, 0.035)); // rear sight
  slide.add(box(0.006, 0.01, 0.006, dark, 0, 0.046, -0.27)); // front sight
  group.add(slide);

  // Frame, dust cover with rail, trigger guard and trigger.
  group.add(box(0.032, 0.026, 0.29, polymer, 0, -0.01, -0.1));
  group.add(box(0.028, 0.008, 0.11, polymer, 0, -0.026, -0.19));
  group.add(box(0.03, 0.006, 0.1, polymer, 0, -0.072, -0.085)); // bottom of the guard
  group.add(box(0.03, 0.05, 0.008, polymer, 0, -0.048, -0.135)); // front of the guard
  const trigger = box(0.008, 0.032, 0.008, steel, 0, -0.04, -0.06);
  trigger.rotation.x = 0.35;
  group.add(trigger);

  // Grip, raked back, with texture panels.
  const grip = new THREE.Group();
  grip.add(box(0.034, 0.15, 0.056, polymer, 0, -0.075, 0));
  grip.add(box(0.036, 0.1, 0.04, dark, 0, -0.08, 0.002));
  grip.add(box(0.03, 0.02, 0.05, polymer, 0, 0.0, 0.01)); // beavertail
  grip.position.set(0, -0.012, 0.02);
  grip.rotation.x = -0.22;
  group.add(grip);

  // Magazine base (drops out during reload).
  const mag = box(0.03, 0.014, 0.05, dark, 0, -0.155, 0.006);
  grip.add(mag);

  // Barrel tip and silencer.
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.03, 12), steel);
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 0.022, -0.29);
  group.add(barrel);
  const silencer = new THREE.Mesh(new THREE.CylinderGeometry(0.017, 0.017, 0.2, 20), dark);
  silencer.rotation.x = Math.PI / 2;
  silencer.position.set(0, 0.022, -0.39);
  group.add(silencer);

  const muzzle = new THREE.Object3D();
  muzzle.position.set(0, 0.022, -0.3);
  group.add(muzzle);
  return { group, slide, mag, silencer, muzzle };
}

/** A gloved hand with fingers curled around a grip (along -Y), and a sleeve going back. */
export function buildArm(): THREE.Group {
  const glove = new THREE.MeshPhongMaterial({ color: 0x23262a, specular: 0x333333, shininess: 14 });
  const knuckle = new THREE.MeshPhongMaterial({ color: 0x30343a, specular: 0x444444, shininess: 20 });
  const sleeve = new THREE.MeshPhongMaterial({ color: 0x3a4430, specular: 0x111111, shininess: 6 });
  const cuff = new THREE.MeshPhongMaterial({ color: 0x2c3326, specular: 0x111111, shininess: 6 });
  const arm = new THREE.Group();

  // Palm on the right side of the grip, fingers wrapping around the front.
  arm.add(box(0.03, 0.09, 0.075, glove, 0.03, -0.06, 0.005));
  for (let i = 0; i < 4; i++) {
    const y = -0.03 - i * 0.022;
    const f = new THREE.Mesh(new THREE.CapsuleGeometry(0.0095, 0.05, 4, 8), i === 0 ? knuckle : glove);
    f.rotation.y = Math.PI / 2;
    f.rotation.z = Math.PI / 2;
    f.position.set(0.004, y, -0.045);
    arm.add(f);
    const tip = new THREE.Mesh(new THREE.CapsuleGeometry(0.009, 0.02, 4, 8), glove);
    tip.rotation.x = Math.PI / 2;
    tip.position.set(-0.024, y, -0.025);
    arm.add(tip);
  }
  // Thumb along the left side.
  const thumb = new THREE.Mesh(new THREE.CapsuleGeometry(0.01, 0.055, 4, 8), glove);
  thumb.rotation.x = Math.PI / 2 - 0.3;
  thumb.position.set(-0.026, -0.008, -0.01);
  arm.add(thumb);
  // Wrist, cuff and sleeve going back and down out of view.
  const wrist = new THREE.Mesh(new THREE.CylinderGeometry(0.034, 0.038, 0.08, 14), glove);
  wrist.rotation.x = Math.PI / 2 - 0.5;
  wrist.position.set(0.03, -0.1, 0.07);
  arm.add(wrist);
  const c = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.05, 0.05, 16), cuff);
  c.rotation.x = Math.PI / 2 - 0.5;
  c.position.set(0.035, -0.125, 0.12);
  arm.add(c);
  const s = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.062, 0.6, 16), sleeve);
  s.rotation.x = Math.PI / 2 - 0.5;
  s.position.set(0.045, -0.26, 0.38);
  arm.add(s);
  return arm;
}

/** Muzzle flash: two crossed additive quads with a radial gradient. */
export function buildMuzzleFlash(): THREE.Group {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, 'rgba(255,250,220,1)');
  grad.addColorStop(0.3, 'rgba(255,190,90,0.9)');
  grad.addColorStop(1, 'rgba(255,120,30,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const flash = new THREE.Group();
  for (const r of [0, Math.PI / 2]) {
    const q = new THREE.Mesh(new THREE.PlaneGeometry(0.09, 0.09), mat);
    q.rotation.z = r;
    flash.add(q);
    const side = new THREE.Mesh(new THREE.PlaneGeometry(0.05, 0.14), mat);
    side.rotation.set(Math.PI / 2, r, 0);
    side.position.z = -0.05;
    flash.add(side);
  }
  flash.visible = false;
  return flash;
}
